<script>
  import { RadioGroup, Label, useId } from "bits-ui";

  /**
   * @typedef {Object} RadioItemProps
   * @property {string} [value] - The value of the radio item.
   * @property {boolean} [disabled=false] - Whether the radio item is disabled.
   * @property {string} [class] - Additional CSS classes for the radio item.
   * @property {string} [children] - Custom content to render inside the radio item.
   * @property {string} [label] - The label for the radio item.
   * @property {"button" | "radio"} [type = "button"] - What type of ui
   */
  let { value, disabled = false, children, label, type = "radio", ...restProps } = $props();
  const id = useId();
</script>

<div class="radio-group-wrapper">
  <RadioGroup.Item
    {id}
    {value}
    {disabled}
    {...restProps}
    class={type === "button" ? "radio-button" : "radio-radio"}
  >
    {#snippet children({ checked })}
      {#if type === "button"}
        <span class="button" class:selected={checked}>{label}</span>
      {:else if checked}
        <div class="radio-inner-item"></div>
      {/if}
    {/snippet}
  </RadioGroup.Item>

  {#if label && type === "radio"}
    <div>
      <Label.Root for={id} class={disabled ? "disabled" : ""}>
        {label}
      </Label.Root>
    </div>
  {/if}
</div>

<style>
  .radio-group-wrapper {
    display: flex;
    align-items: center;
    gap: var(--spacing-2);
  }
  .radio-group-wrapper:not(:first-child) {
    margin-top: var(--spacing-2);
  }
  .button {
    display: inline-block;
    cursor: pointer;
    padding: var(--spacing-2);
    color: black;
    transition: all 750ms ease;
    background: var(--color-gray-shade-light);
  }
  .button:hover,
  .button.selected:hover {
    background: var(--color-blue);
    color: var(--color-white);
  }
  .button.selected {
    background: var(--color-blue);
    color: white;
  }
  .radio-group-wrapper :global(.radio-button) {
    margin-bottom: var(--spacing-2);
    background: var(--color-blue);
    color: white;
  }
  .radio-group-wrapper :global(.radio-radio) {
    border: solid 2px var(--color-gray-shade-darker);
    background: var(--color-white);
    height: var(--spacing-5);
    width: var(--spacing-5);
    cursor: default;
    border-radius: 50%;
    transition: all 100ms ease-in-out;
    flex-shrink: 0;
    padding: 0;
  }
  .radio-group-wrapper :global(.radio-radio:focus) {
    outline: none;
  }
  .radio-group-wrapper :global(.radio-radio:focus-visible) {
    outline: solid 2px var(--color-blue-shade-dark);
  }
  .radio-group-wrapper :global(.radio-radio[disabled]) {
    border: solid 2px var(--color-gray-shade-light);
  }
  .radio-group-wrapper :global(.radio-radio[disabled]:hover) {
    border: solid 2px var(--color-gray-shade-light);
  }
  .radio-group-wrapper :global(.radio-radio:hover) {
    border: solid 2px var(--color-gray-shade-dark);
  }
  .radio-group-wrapper :global(.radio-radio[data-state="checked"]) {
    border: solid 2px var(--color-blue);
  }
  .radio-group-wrapper :global(.radio-inner-item) {
    margin: auto;
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background: var(--color-blue);
  }
  .radio-group-wrapper :global(label.disabled) {
    color: var(--color-gray-shade-dark);
  }
</style>
